import { useState, useEffect, useContext } from 'react';
import { Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttFormSearch,
    SttMshipList,
    SttDialog,
    SttButton,
} from '../all';
import UserCreationDialog from '../../components/dialogs/UserCreationDialog';

type Mship = {
    id: number;
    fullName?: string;
    user?: {
        id?: number;
        login?: string;
    };
    [key: string]: unknown;
};

type MshipsResponse = {
    rows: Mship[];
    count: number;
};

type Props = {
    open?: boolean;
    onClose?: () => void;
    onSelectMship?: (mship: Mship) => void;
};

/**
 *
 * Props
 *
 *     * boolean open
 *     * function onClose
 *     * function onSelectMship(mship)
 *
 */
export function SttSelectMshipDialog({
    open = false,
    onClose,
    onSelectMship,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [mships, setMships] = useState<MshipsResponse | undefined>();
    const [page, setPage] = useState<number>(1);
    const [keywordSearch, setKewordSearch] = useState<string>('');
    const [creatingUserDialogOpen, setCreatingUserDialogOpen] = useState(false);

    /**
     * Load mships
     */
    const loadMships = (p: { page?: number } = { page: 1 }) => {
        const currPage = p.page !== undefined ? p.page : page;
        cxt?.api('GET', `/scs/${cxt?.sc?.id}/mships`, {
            params: {
                page: currPage,
                rows: 6,
                keyword: keywordSearch,
            },
            success: (r: any) => {
                setMships(r);
                setPage(currPage);
            },
        });
    };

    /**
     * Open
     */
    useEffect(() => {
        if (open && !mships) {
            loadMships();
        }
    }, [open]);

    // -----| Render |-----
    return (
        <SttDialog
            title={t('Users')}
            open={open}
            onClose={onClose}
            content={
                <Box>
                    <Box>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 8 }}>
                                <SttFormSearch
                                    defVal={keywordSearch}
                                    onClickSearch={() =>
                                        loadMships({ page: 1 })
                                    }
                                    onChange={({
                                        value,
                                    }: {
                                        name: string | undefined;
                                        value: unknown;
                                    }) => {
                                        setKewordSearch(value as string);
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 4 }}>
                                <SttButton
                                    caption={t('create.user')}
                                    onClick={() =>
                                        setCreatingUserDialogOpen(true)
                                    }
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <SttMshipList
                        mships={mships as any}
                        page={page}
                        onSelect={onSelectMship ?? (() => {})}
                        onLoadMships={loadMships}
                    />

                    <UserCreationDialog
                        open={creatingUserDialogOpen}
                        onClose={() => {
                            setCreatingUserDialogOpen(false);
                            loadMships();
                        }}
                    />
                </Box>
            }
        />
    );
}
